import { useLanguage } from '@/contexts/LanguageContext';
import { CheckCircle2, Clock, AlertTriangle, Circle } from 'lucide-react';

const STAGES = [
  { key: 'tour', en: 'Tour', es: 'Tour' },
  { key: 'onboarding', en: 'Documents', es: 'Documentos' },
  { key: 'poa', en: 'POA', es: 'Poder' },
  { key: 'due_diligence', en: 'Due Diligence', es: 'Debida Diligencia' },
  { key: 'offer', en: 'Offer', es: 'Oferta' },
  { key: 'contract', en: 'Contract', es: 'Contrato' },
  { key: 'payment', en: 'Payment', es: 'Pago' },
  { key: 'signing', en: 'Signing', es: 'Firma' },
  { key: 'registry', en: 'Registry', es: 'Registro' },
  { key: 'complete', en: 'Complete', es: 'Completo' },
  { key: 'delivery', en: 'Delivery', es: 'Entrega' },
];

const COMPLETED_STATES = {
  tour: ['completed', 'skipped'],
  onboarding: ['profile_verified', 'profile_submitted'],
  poa: ['poa_verified', 'poa_uploaded'],
  due_diligence: ['completed'],
  offer: ['accepted'],
  contract: ['approved'],
  payment: ['confirmed'],
  signing: ['completed'],
  registry: ['registered'],
  complete: ['ownership_confirmed'],
  delivery: ['delivered'],
};

const IN_PROGRESS_STATES = {
  tour: ['requested', 'scheduled'],
  onboarding: ['profile_submitted'],
  poa: ['poa_uploaded'],
  due_diligence: ['in_progress'],
  offer: ['submitted', 'counter_offer'],
  contract: ['draft_uploaded', 'under_review', 'changes_requested'],
  payment: ['proof_uploaded'],
  signing: ['in_progress'],
  registry: ['submitted'],
  complete: ['pending'],
  delivery: ['shipment_pending', 'in_transit'],
};

const ERROR_STATES = {
  due_diligence: ['failed'],
  offer: ['rejected'],
  payment: ['flagged'],
};

function getStageColor(stageKey, stages) {
  const status = stages?.[stageKey]?.status;
  if (!status) return 'grey';
  if (COMPLETED_STATES[stageKey]?.includes(status)) return 'green';
  if (ERROR_STATES[stageKey]?.includes(status)) return 'red';
  if (IN_PROGRESS_STATES[stageKey]?.includes(status)) return 'yellow';
  return 'grey';
}

const colorMap = {
  grey: { bg: 'bg-slate-200', ring: 'border-slate-200', text: 'text-slate-400', line: 'bg-slate-200', icon: Circle },
  yellow: { bg: 'bg-amber-100', ring: 'border-amber-400', text: 'text-amber-600', line: 'bg-amber-300', icon: Clock },
  green: { bg: 'bg-green-100', ring: 'border-green-500', text: 'text-green-600', line: 'bg-green-400', icon: CheckCircle2 },
  red: { bg: 'bg-red-100', ring: 'border-red-500', text: 'text-red-600', line: 'bg-red-400', icon: AlertTriangle },
};

export default function TransactionTimeline({ stages, currentStage }) {
  const { language } = useLanguage();

  const STATUS_ES = {
    idle: 'Inactivo', requested: 'Solicitado', scheduled: 'Programado', completed: 'Completado', skipped: 'Omitido',
    profile_incomplete: 'Perfil Incompleto', profile_submitted: 'Perfil Enviado', profile_verified: 'Perfil Verificado',
    poa_missing: 'Poder Faltante', poa_uploaded: 'Poder Subido', poa_verified: 'Poder Verificado',
    pending: 'Pendiente', in_progress: 'En Progreso', failed: 'Fallido',
    not_started: 'No Iniciado', submitted: 'Enviada', accepted: 'Aceptada', rejected: 'Rechazada', counter_offer: 'Contra Oferta',
    draft_uploaded: 'Borrador Subido', under_review: 'En Revision', changes_requested: 'Cambios Solicitados', approved: 'Aprobado',
    proof_uploaded: 'Comprobante Subido', confirmed: 'Confirmado', flagged: 'Marcado',
    registered: 'Registrado', ownership_confirmed: 'Propiedad Confirmada',
    shipment_pending: 'Envio Pendiente', in_transit: 'En Transito', delivered: 'Entregado',
  };
  const ts = (status) => {
    if (!status) return '';
    if (language === 'es') return STATUS_ES[status] || status.replace(/_/g, ' ');
    return status.replace(/_/g, ' ');
  };

  return (
    <div className="bg-white rounded-xl border border-border p-4 sticky top-4" data-testid="transaction-timeline">
      <h3 className="font-heading font-semibold text-sm mb-4 text-slate-700">
        {language === 'es' ? 'Progreso de la Transaccion' : 'Transaction Progress'}
      </h3>
      <div className="space-y-0">
        {STAGES.map((stage, i) => {
          const color = getStageColor(stage.key, stages);
          const c = colorMap[color];
          const Icon = c.icon;
          const isLast = i === STAGES.length - 1;
          const isCurrent = stage.key === currentStage;
          return (
            <div key={stage.key} className="flex items-start gap-3" data-testid={`timeline-stage-${stage.key}`}>
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full border-2 ${c.ring} ${c.bg} flex items-center justify-center flex-shrink-0 ${isCurrent ? 'ring-2 ring-offset-1 ring-primary/30' : ''}`}>
                  <Icon className={`w-3.5 h-3.5 ${c.text}`} />
                </div>
                {!isLast && <div className={`w-0.5 h-6 ${c.line}`} />}
              </div>
              <div className={`pb-5 ${isCurrent ? '-mt-0.5' : '-mt-0.5'}`}>
                <p className={`text-xs font-semibold ${isCurrent ? 'text-primary' : c.text}`}>
                  {stage[language]}
                </p>
                {stages?.[stage.key]?.status && (
                  <p className="text-[10px] text-slate-400 capitalize mt-0.5">
                    {ts(stages[stage.key].status)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { STAGES, getStageColor };
